import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Copy, Mail, RefreshCw, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailHealth = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("");
  const [test, setTest] = useState(null);
  const [sending, setSending] = useState(false);

  const sendTest = async () => {
    setSending(true);
    setTest(null);
    try {
      const { data: r } = await axios.post(
        `${API}/admin/email-test`,
        { to },
        { withCredentials: true },
      );
      setTest(r);
      toast[r.sent ? "success" : "error"](
        r.sent ? `Test email sent to ${r.to}` : `Send failed: ${r.error || r.reason}`,
        { duration: 9000 },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the test email");
    } finally {
      setSending(false);
    }
  };

  const [dns, setDns] = useState(null);
  const [validating, setValidating] = useState(false);

  const loadDns = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/email-dns`, { withCredentials: true });
      setDns(d);
    } catch {
      setDns(null);
    }
  }, []);

  const validateDns = async () => {
    setValidating(true);
    try {
      const { data: d } = await axios.post(
        `${API}/admin/email-dns/validate`,
        {},
        { withCredentials: true },
      );
      setDns(d);
      toast[d.valid ? "success" : "error"](
        d.valid
          ? `${d.domain} is authenticated — DKIM signing is live`
          : d.error || "DNS records not visible yet",
        { duration: 9000 },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not validate DNS");
    } finally {
      setValidating(false);
    }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/email-health`, { withCredentials: true });
      setData(d);
    } catch {
      setData(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadDns();
  }, [load, loadDns]);

  const ok = data?.can_send_to_anyone;

  return (
    <div
      data-testid="email-health"
      className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#06B6D4]" />
          <h3 className="font-display text-base font-semibold tracking-tight">Email delivery health</h3>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          data-testid="email-health-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Re-check
        </button>
      </div>

      {data && (
        <>
          <div
            data-testid="email-health-status"
            className={`mt-4 flex items-start gap-2.5 rounded-xl border p-3.5 text-sm ${
              ok
                ? "border-emerald-200 dark:border-emerald-500/25 bg-emerald-50 dark:bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
                : "border-amber-200 dark:border-amber-500/25 bg-amber-50 dark:bg-amber-500/5 text-amber-700 dark:text-amber-300"
            }`}
          >
            {ok ? (
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            )}
            <span>
              {ok
                ? "Emails can be delivered to any student address."
                : data.error || "Emails are currently restricted."}
              {!ok && (
                <span className="block mt-1 text-xs opacity-80">
                  SendGrid will not deliver anything until {data.from_email} is a verified sender.
                  Go to SendGrid → Settings → Sender Authentication → Single Sender Verification, add{" "}
                  {data.from_email}, click the confirm link in that inbox → re-check here.
                </span>
              )}
            </span>
          </div>

          <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Sender</dt>
              <dd className="font-medium truncate" data-testid="email-health-from">
                {data.from_email}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">API key</dt>
              <dd className="font-medium">{data.key_valid ? "Valid" : "Invalid / missing"}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Sender verified</dt>
              <dd className="font-medium" data-testid="email-health-sender-verified">
                {data.sender_verified ? "Yes" : "No"}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Domain</dt>
              <dd className="font-medium">{data.from_domain}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Domain auth status</dt>
              <dd className="font-medium" data-testid="email-health-domain-status">
                {data.domain_status || "not added"}
              </dd>
            </div>
          </dl>

          {data.domains?.length > 0 && (
            <p className="mt-3 text-[11px] text-slate-500">
              Authenticated domains in this SendGrid account:{" "}
              {data.domains.map((d) => `${d.name} (${d.status})`).join(" · ")}
            </p>
          )}

          {dns && (
            <div
              data-testid="email-dns"
              className="mt-4 pt-4 border-t border-slate-100 dark:border-white/5"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                  Domain authentication · {dns.domain}
                </div>
                <button
                  type="button"
                  onClick={validateDns}
                  disabled={validating}
                  data-testid="email-dns-validate"
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs disabled:opacity-50"
                >
                  <ShieldCheck className={`h-3 w-3 ${validating ? "animate-pulse" : ""}`} />
                  {validating ? "Checking DNS…" : "Validate DNS"}
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500" data-testid="email-dns-state">
                {dns.valid
                  ? "Verified — DKIM signing is active, mail is signed by your own domain."
                  : dns.error ||
                    "Add these 3 CNAME records at your DNS provider (Cloudflare → DNS → Records). Turn the orange cloud OFF / set Proxy status to DNS only, then hit Validate DNS."}
              </p>
              <div className="mt-2.5 space-y-2">
                {(dns.records || []).map((r) => (
                  <div
                    key={r.host}
                    className="rounded-xl border border-slate-200 dark:border-white/10 p-2.5 text-[11px]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">
                        {r.label} · {r.type}
                      </span>
                      <span className={r.valid ? "text-emerald-500" : "text-amber-500"}>
                        {r.valid ? "verified" : "pending"}
                      </span>
                    </div>
                    <div className="mt-1 grid gap-1">
                      <div className="flex items-start gap-1.5">
                        <span className="text-slate-500 w-12 shrink-0">Name</span>
                        <code className="break-all">{r.host}</code>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(r.host)}
                          className="text-slate-400 hover:text-slate-600"
                          title="Copy name"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <span className="text-slate-500 w-12 shrink-0">Value</span>
                        <code className="break-all">{r.value}</code>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(r.value)}
                          className="text-slate-400 hover:text-slate-600"
                          title="Copy value"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Send a test email
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              Send to any address — once the sender above is verified, SendGrid delivers to every
              student inbox (free tier: 100 emails/day).
            </p>
            <div className="mt-2.5 flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="student@example.com"
                data-testid="email-test-to"
                className="flex-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
              />
              <button
                type="button"
                onClick={sendTest}
                disabled={sending || !to}
                data-testid="email-test-send"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send test"}
              </button>
            </div>
            {test && (
              <p
                data-testid="email-test-result"
                className={`mt-2.5 text-[11px] ${test.sent ? "text-emerald-500" : "text-rose-500"}`}
              >
                {test.sent
                  ? `Delivered to ${test.to} from ${test.from_email}. Student emails will arrive.`
                  : `Failed: ${test.error || test.reason || "unknown error"}`}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
